import * as d3 from 'd3';
import _ from 'underscore';

import {SliceComponentName} from 'constants/MapsConstants';
import {PolymerAlignmentTabState} from 'molbio/maps/alignment/PolymerAlignmentTabView';
import Component, {ComponentOptions} from 'molbio/maps/linear/Component';
import {Line} from 'molbio/maps/linear/LinearLayout';
import {LineRangePoints} from 'molbio/maps/linear/PathBuilder';
import Slice from 'molbio/maps/linear/Slice';
import {AlignmentMapState, SummaryMapState} from 'molbio/maps/MapStates';
import * as vo from 'settings/visibility-options/VisibilityOptions';

interface LineViewInitializationOptions<TRowState> extends ComponentOptions {
  setting: string;
  tabstate: PolymerAlignmentTabState<TRowState>;
  visibilityOptions: vo.VisibilityOptions;
  lineLayout: Line;
  slice: Slice;
  behind: boolean;
  name: SliceComponentName.line;
}

class LineView<TRowState> extends Component {
  tabstate!: PolymerAlignmentTabState<TRowState>;
  slice!: Slice;
  layout!: Line;
  behind!: boolean;

  static override initClass(): void {
    this.prototype.className = 'line';
    this.prototype.setting = vo.LINE.name;
  }

  override initialize(options: LineViewInitializationOptions<TRowState>): void {
    this.tabstate = options.tabstate; // must be alignment tab state
    this.slice = options.slice;
    this.layout = options.lineLayout;
    this.behind = options.behind;
    if (this.behind) {
      this.layout.setBehind();
    }
    super.initialize(options);
  }

  override registerListeners(): void {
    super.registerListeners();
    if (this.model instanceof SummaryMapState || this.model instanceof AlignmentMapState) {
      // @ts-expect-error: TS cannot merge method calls like this, but it does in fact return a Sequence instance
      this.listenTo(this.model.get('sequence').get('_sequence'), 'renderLineRanges', () => {
        this.update(false);
      });
    }
  }

  getPath(
    xTransform: (value: number) => number,
    yTransform: (value: number) => number
  ): {correctPath: string; mutationPath: string; unknownPath: string} {
    const lineRanges = this.tabstate.getLineRanges(this.model as TRowState);
    return {
      correctPath: this._getDataString(lineRanges.correct, xTransform, yTransform),
      mutationPath: this._getDataString(lineRanges.mutation, xTransform, yTransform),
      unknownPath: this._getDataString(lineRanges.unknown, xTransform, yTransform),
    };
  }

  _getDataString(
    points: LineRangePoints,
    xTransform: (value: number) => number,
    yTransform: (value: number) => number
  ): string {
    let path = '';
    for (const set of points) {
      path += `M${xTransform(set[0][0])},${yTransform(set[0][1])} `;
      let lastY = -1;
      let refX = -1;
      for (const point of set.slice(1)) {
        const thisX = Math.max(refX + 1, xTransform(point[0]));
        const thisY = yTransform(point[1]);
        if (thisY !== lastY) {
          refX = xTransform(point[0]);
        }
        path += `L${thisX},${thisY} `;
        lastY = thisY;
      }
    }
    return path;
  }

  override shouldShow(): boolean {
    return this.option!.get('visible');
  }

  update(trigger: boolean = true): void {
    const x = this.slice.x();
    const height = this.behind ? this.tabstate.getType().height() : this.layout.height();
    const y = d3.scaleLinear().domain([0, 1]).range([height, 0]);

    // eslint-disable-next-line no-restricted-syntax
    const paths = _.filter(_.pairs(this.getPath(x, y)), (pair) => pair[1].length > 0);
    const root = d3.select(this.el).select('g').selectAll('path').data(paths);

    const correctColor = this.layout.get('correctFillColor');
    const mutationColor = this.layout.get('mutationFillColor');
    const unknownValueColor = this.layout.get('unknownValueFillColor');
    root
      .join('svg:path')
      .attr('d', (d) => d[1])
      .attr('fill', (d) => {
        if (d[0] === 'correctPath') {
          return correctColor;
        } else if (d[0] === 'unknownPath') {
          return unknownValueColor;
        } else {
          return mutationColor;
        }
      })
      .attr('fill-opacity', this.layout.get('fillOpacity'))
      .attr('data-test-component', 'LineView')
      .attr('data-test-element', 'path');
    if (trigger) {
      this.trigger('redraw');
    }
  }
}
LineView.initClass();

export default LineView;
